var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Field, Form, Formik } from "formik";
import { PeekButton, PeekDialogActions, PeekDialogContent, PeekDivider, PeekStack, useTheme, } from "@piiqtechnologies/ui-components";
import { useAnalytics, useCreateUser, useError, useLoader, useNotification, useRefetch, useUpdateUser, } from "@piiqtechnologies/ui-lib";
import { MultiColumnFormFields } from "@components/common/form/FormFields";
import { useAuthContext } from "@stores/AuthContext";
import { DISCOVER_ROLES, DiscoverRoleAliases, USER_ROLES } from "@constants/Roles";
import messages from "./messages.i18n";
import { CreateOrEditUserFormFields, UserFormValidationSchema } from "./CreateOrEditUserFormConfig";
import { PolicyGroups } from "@constants/Policies";
import { ProductAccessSection } from "./user-type-selector/utils/product-access-section";
import { DiscoverAccessSelector } from "./user-type-selector/utils/discover-access";
import { InsightsOrganizationIds } from "./user-type-selector/utils/insights-access";
import { LeasingAccessSection } from "./user-type-selector/utils/leasing-access";
import { OrganizationSearchSelector } from "@components/common/organization-search-selector";
import { MultipleOrganizationsSelector } from "@components/admin/common/multiple-organizations-selector";
const BasicUserAllow = {
    insights: [],
};
const BasicUserDeny = {
    scanRequest: ["scan_request_edit" /* PolicyActions.SCAN_REQUEST_EDIT */, "scan_request_create" /* PolicyActions.SCAN_REQUEST_CREATE */],
    analytics: [],
    prospectIntelligence: [],
};
export const initializeAllow = (allow) => {
    return (allow || [])
        .map((action) => action.alias)
        .reduce((acc, action) => {
        if (PolicyGroups.INSIGHTS.includes(action)) {
            acc["insights"].push(action);
        }
        return acc;
    }, {
        insights: [],
    });
};
export const initializeDeny = (deny) => {
    return (deny || [])
        .map((action) => action.alias)
        .reduce((acc, action) => {
        if (PolicyGroups.SCAN_REQUEST.includes(action)) {
            acc["scanRequest"].push(action);
        }
        if (PolicyGroups.ANALYTICS.includes(action)) {
            acc["analytics"].push(action);
        }
        if (PolicyGroups.PROSPECT_INTELLIGENCE.includes(action)) {
            acc["prospectIntelligence"].push(action);
        }
        return acc;
    }, {
        scanRequest: [],
        analytics: [],
        prospectIntelligence: [],
    });
};
const flattenPolicyGroups = (policyGroups) => Object.values(policyGroups || {}).reduce((acc, curr) => [...acc, ...curr], []);
// The backend replaces the stored deny list wholesale, so an edit must only send
// it when the admin actually changed a permission; otherwise an unrelated save
// would overwrite the user's real denies with whatever the form loaded.
export const hasDenyChanged = (initialDeny, currentDeny) => {
    const initialActions = new Set(flattenPolicyGroups(initialDeny));
    const currentActions = new Set(flattenPolicyGroups(currentDeny));
    return (initialActions.size !== currentActions.size || [...currentActions].some((action) => !initialActions.has(action)));
};
export const CreateOrEditUserForm = ({ isUserCreation = false, isAdminForm = false, onSubmit = () => ({}), userFormInitialValues, }) => {
    var _a, _b;
    const intl = useIntl();
    const theme = useTheme();
    const analytics = useAnalytics();
    const { user } = useAuthContext();
    const [createUser, userCreated, createUserInProgress, createUserError] = useCreateUser();
    const [updateUser, userUpdated, updateUserInProgress, updateUserError] = useUpdateUser();
    const [openCreateUser, setOpenCreateUser] = useState(false);
    const [isOwnerTypeForm, setIsOwnerTypeForm] = useState((userFormInitialValues === null || userFormInitialValues === void 0 ? void 0 : userFormInitialValues.roleId) === USER_ROLES.Owner);
    const { setError } = useError();
    const { setLoader } = useLoader();
    const { setRefetch } = useRefetch();
    const { setNotification } = useNotification();
    const { createUser_row1, createUser_row2 } = CreateOrEditUserFormFields({
        organizations: (user === null || user === void 0 ? void 0 : user.organizations) || [],
        communities: (user === null || user === void 0 ? void 0 : user.communities) || [],
        isAdminForm: isAdminForm,
        isOwnerTypeForm: isOwnerTypeForm,
    });
    const toggleCreateUser = () => {
        setOpenCreateUser(!openCreateUser);
    };
    const onFormSubmit = (values) => {
        var _a, _b;
        const { _id, deny, allow, discoverAccess, discoverRoleId, discoverOrganizations, insightsAccess, insightsOrganizations, leasingAccess } = values, userValues = __rest(values, ["_id", "deny", "allow", "discoverAccess", "discoverRoleId", "discoverOrganizations", "insightsAccess", "insightsOrganizations", "leasingAccess"]);
        let organizations = [];
        const shouldSendDeny = isUserCreation || hasDenyChanged(userFormInitialValues === null || userFormInitialValues === void 0 ? void 0 : userFormInitialValues.deny, deny);
        if (isAdminForm) {
            if (leasingAccess && userValues.roleId !== USER_ROLES.Insights) {
                organizations =
                    userValues.roleId === USER_ROLES.Owner
                        ? values.organizations
                        : ((_a = values.organizations) === null || _a === void 0 ? void 0 : _a._id)
                            ? [values.organizations._id]
                            : [];
            }
            const getDiscoverRoleId = () => {
                if (!discoverAccess || !discoverRoleId)
                    return null;
                if (discoverRoleId === DiscoverRoleAliases.VIEWER)
                    return DISCOVER_ROLES.Viewer;
                if (discoverRoleId === DiscoverRoleAliases.ADMIN)
                    return DISCOVER_ROLES.Admin;
                return null;
            };
            const userFormPayload = Object.assign(Object.assign(Object.assign(Object.assign({}, userValues), { organizations }), (shouldSendDeny &&
                leasingAccess &&
                (userValues.roleId === USER_ROLES.Basic || userValues.roleId === USER_ROLES.Agent)
                ? { deny: flattenPolicyGroups(deny) }
                : {})), { allow: insightsAccess
                    ? ["insights_access" /* PolicyActions.INSIGHTS_ACCESS */]
                    : !!allow
                        ? Object.values(allow).reduce((acc, curr) => [...acc, ...curr], [])
                        : [], discoverRoleId: getDiscoverRoleId(), discoverOrganizations: discoverAccess ? discoverOrganizations === null || discoverOrganizations === void 0 ? void 0 : discoverOrganizations.filter((id) => (id === null || id === void 0 ? void 0 : id.trim()) !== "") : [], insightsOrganizations: insightsAccess ? insightsOrganizations === null || insightsOrganizations === void 0 ? void 0 : insightsOrganizations.filter((id) => (id === null || id === void 0 ? void 0 : id.trim()) !== "") : [], roleId: leasingAccess ? userValues.roleId : null });
            if (isUserCreation) {
                createUser(userFormPayload);
                return;
            }
            updateUser(Object.assign({ _id: _id }, userFormPayload));
            return;
        }
        if (userValues.roleId && userValues.roleId !== USER_ROLES.Insights) {
            organizations =
                userValues.roleId === USER_ROLES.Owner
                    ? values.organizations
                    : ((_b = values.organizations) === null || _b === void 0 ? void 0 : _b._id)
                        ? [values.organizations._id]
                        : [];
        }
        const userFormPayload = Object.assign(Object.assign(Object.assign(Object.assign({}, userValues), { organizations }), (shouldSendDeny && (userValues.roleId === USER_ROLES.Basic || userValues.roleId === USER_ROLES.Agent)
            ? { deny: flattenPolicyGroups(deny) }
            : {})), { allow: !!allow ? Object.values(allow).reduce((acc, curr) => [...acc, ...curr], []) : [] });
        if (isUserCreation) {
            createUser(userFormPayload);
            return;
        }
        updateUser(Object.assign({ _id: _id }, userFormPayload));
    };
    useEffect(() => {
        setLoader(createUserInProgress, "createUserInProgress");
        return () => setLoader(false, "createUserInProgress");
    }, [createUserInProgress]);
    useEffect(() => {
        setLoader(updateUserInProgress, "updateUserInProgress");
        return () => setLoader(false, "updateUserInProgress");
    }, [updateUserInProgress]);
    const getErrorMessage = (error) => {
        var _a, _b, _c;
        const payload = (_a = error === null || error === void 0 ? void 0 : error.cause) === null || _a === void 0 ? void 0 : _a.data;
        if (Array.isArray(payload) && payload.length > 0) {
            const validationMessage = (_c = (_b = payload[0]) === null || _b === void 0 ? void 0 : _b.context) === null || _c === void 0 ? void 0 : _c.message;
            if (validationMessage) {
                return validationMessage;
            }
        }
        return error === null || error === void 0 ? void 0 : error.message;
    };
    useEffect(() => {
        if (createUserError) {
            setError(getErrorMessage(createUserError));
        }
    }, [createUserError]);
    useEffect(() => {
        if (updateUserError) {
            setError(getErrorMessage(updateUserError));
        }
    }, [updateUserError]);
    useEffect(() => {
        if (userCreated || userUpdated) {
            toggleCreateUser();
            analytics.track(isUserCreation ? "User Created" : "User Updated");
            setNotification(isUserCreation
                ? intl.formatMessage(messages.createUserSuccess)
                : intl.formatMessage(messages.updateUserSuccess));
            onSubmit();
            setRefetch();
        }
    }, [userCreated, userUpdated]);
    const UserFormInitialValues = userFormInitialValues || Object.assign({ roleId: USER_ROLES.Basic, name: "", email: "", phone: "", fullAccess: true, communities: [], allow: BasicUserAllow, deny: BasicUserDeny, organizations: !isAdminForm && user.role && { _id: (_b = (_a = user.organizations) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b._id } }, (isAdminForm && {
        discoverAccess: false,
        discoverRoleId: null,
        discoverOrganizations: [],
        insightsAccess: false,
        insightsOrganizations: [],
        leasingAccess: true,
    }));
    return (React.createElement(Formik, { initialValues: UserFormInitialValues, validationSchema: UserFormValidationSchema(isAdminForm), onSubmit: onFormSubmit }, ({ values, setFieldValue }) => {
        useEffect(() => {
            setIsOwnerTypeForm(values.roleId === USER_ROLES.Owner);
        }, [values.roleId]);
        const handleDiscoverAccessToggle = () => {
            const newValue = !values.discoverAccess;
            setFieldValue("discoverAccess", newValue);
            if (newValue && !values.discoverRoleId) {
                setFieldValue("discoverRoleId", DiscoverRoleAliases.VIEWER);
            }
        };
        const handleLeasingAccessToggle = () => {
            const newValue = !values.leasingAccess;
            setFieldValue("leasingAccess", newValue);
        };
        const handleDiscoverRoleChange = (role) => {
            setFieldValue("discoverRoleId", role);
            if (!values.discoverOrganizations || values.discoverOrganizations.length === 0) {
                setFieldValue("discoverOrganizations", [""]);
            }
        };
        const handleDiscoverOrganizationsChange = (ids) => {
            setFieldValue("discoverOrganizations", ids);
        };
        const handleInsightsAccessToggle = () => {
            const newValue = !values.insightsAccess;
            setFieldValue("insightsAccess", newValue);
            if (newValue && (!values.insightsOrganizations || values.insightsOrganizations.length === 0)) {
                setFieldValue("insightsOrganizations", [""]);
            }
        };
        const handleInsightsOrganizationsChange = (ids) => {
            setFieldValue("insightsOrganizations", ids);
        };
        const showDiscoverValidationError = isAdminForm &&
            values.discoverAccess &&
            values.discoverRoleId &&
            (!values.discoverOrganizations ||
                values.discoverOrganizations.length === 0 ||
                values.discoverOrganizations.every((id) => !(id === null || id === void 0 ? void 0 : id.trim())));
        return (React.createElement(Form, null,
            React.createElement(PeekDialogContent, null,
                React.createElement(PeekStack, { direction: "column", spacing: theme.spacing(1) },
                    React.createElement(MultiColumnFormFields, { fullWidth: true, fields: createUser_row1 }),
                    isAdminForm ? (React.createElement(React.Fragment, null,
                        React.createElement(PeekDivider, { light: true }),
                        React.createElement(ProductAccessSection, { isEnabled: values.leasingAccess || false, onToggle: handleLeasingAccessToggle, label: intl.formatMessage(messages.allowAccessToLeasing) },
                            values.roleId !== USER_ROLES.Insights &&
                                (values.roleId === USER_ROLES.Owner ? (React.createElement(Field, { name: "organizations", label: intl.formatMessage(messages.userFormOrganization), placeholder: intl.formatMessage(messages.userFormOrganizationPlaceholder), component: MultipleOrganizationsSelector })) : (React.createElement(Field, { name: "organizations", label: intl.formatMessage(messages.userFormOrganization), placeholder: intl.formatMessage(messages.userFormOrganizationPlaceholder), component: OrganizationSearchSelector }))),
                            React.createElement(Field, { name: "roleId" }, ({ field, form }) => (React.createElement(LeasingAccessSection, { field: field, form: form, label: intl.formatMessage(messages.userRole), isAdminForm: isAdminForm })))),
                        React.createElement(ProductAccessSection, { isEnabled: values.discoverAccess || false, onToggle: handleDiscoverAccessToggle, label: intl.formatMessage(messages.allowAccessToDiscover) },
                            React.createElement(DiscoverAccessSelector, { selectedRole: values.discoverRoleId || null, onRoleChange: handleDiscoverRoleChange, organizationIds: values.discoverOrganizations || [], onOrganizationIdsChange: handleDiscoverOrganizationsChange })),
                        React.createElement(ProductAccessSection, { isEnabled: values.insightsAccess || false, onToggle: handleInsightsAccessToggle, label: intl.formatMessage(messages.allowAccessToInsights) },
                            React.createElement(InsightsOrganizationIds, { organizationIds: values.insightsOrganizations || [], onOrganizationIdsChange: handleInsightsOrganizationsChange })))) : (React.createElement(React.Fragment, null,
                        React.createElement(PeekDivider, { light: true }),
                        React.createElement(MultiColumnFormFields, { fullWidth: true, fields: createUser_row2 }))))),
            React.createElement(PeekDialogActions, null,
                React.createElement(PeekButton, { size: "large", type: "submit", color: "primary", variant: "contained", fullWidth: true }, isUserCreation
                    ? intl.formatMessage(messages.createUserAction)
                    : intl.formatMessage(messages.editUserAction)))));
    }));
};
export default CreateOrEditUserForm;
